import React, { useContext, useState } from 'react';

import { ListItemText, MenuItem, Tooltip } from '@material-ui/core';

import ActionMenu from '@/components/ActionMenu';
import { Avatar } from '@/components/Avatar';
import { User } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import ZendeskWidget from '@/core/integrations/ZendeskWidget';
import { UserPermissionsContentWidth } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import { ReactComponent as ArrowRightBoxed } from '@/icons/arrow_right_boxed.svg';
import { ReactComponent as QuestionIcon } from '@/icons/question.svg';
import { ReactComponent as UserVoiceLogomark } from '@/icons/uservoice_logomark.svg';
import { ReactComponent as ValidationLogo } from '@/icons/validation_logo_simple.svg';

const navIconContainer = 'flex h-8 w-8 mr-2 rounded bg-blue-200 cursor-pointer';
const navIcon = 'm-auto w-4 h-4 text-blue-900';

type Props = {
  children: JSX.Element;
}
export function UsersNavigationBar ({ children }: Props) {
  const { currentUser, hasCoreAccess, hasUvkAccess, logout } = useContext(AuthContext);
  const [showZendeskWidget, setShowZendeskWidget] = useState(false);
  const [useAvatarHighlight, setUseAvatarHighlight] = useState(false);

  const user: User = {
    id: currentUser!.id,
    displayName: currentUser!.displayName,
    email: currentUser!.email,
    avatarUrl: currentUser!.avatarUrl,
    lastLogin: currentUser!.lastLogin,
    permissions: {},
    teams: [],
  };

  return (
    <nav className="w-full bg-white">
      <UserPermissionsContentWidth className="flex items-center justify-between h-full">
        { children }

        <div className="flex items-center">
          <Tooltip title="Help">
            <button className={`evt-open-zendesk-widget ${navIconContainer}`} onClick={() => setShowZendeskWidget(true)}>
              <QuestionIcon className={navIcon}/>
            </button>
          </Tooltip>
          <ZendeskWidget
            show={showZendeskWidget}
            onClose={() => setShowZendeskWidget(false)}
          />
          <ActionMenu
            id="action-menu-nav"
            popoverClasses={{ paper: 'bg-white shadow-soft' }}
            onFocus={() => setUseAvatarHighlight(true)}
            onBlur={() => setUseAvatarHighlight(false)}
            renderButton={() => (
              <div className="p-2">
                <div
                  className={`flex items-center justify-center rounded-full ${useAvatarHighlight ? 'bg-gray-100' : ''}`}
                  style={{
                    width: '3.25rem',
                    height: '3.25rem',
                    boxShadow: useAvatarHighlight ? '4px 4px 7px rgba(110, 163, 238, 0.29)' : undefined,
                  }}
                >
                  <div className="w-10 h-10">
                    <Avatar user={user} />
                  </div>
                </div>
              </div>
            )}
          >
            {(closeMenu => ([
              <MenuItem
                className={`border-b-0 border-t-0 h-14 evt-navbar-back-to-validation ${hasUvkAccess ? '' : 'hidden'}`}
                key="back-to-validation"
                component="a"
                href="/roadmap/admin"
                onClick={closeMenu}
              >
                <ValidationLogo className="w-5 h-5 mr-2" />
                <ListItemText primary="Back to Validation" />
              </MenuItem>,
              <MenuItem
                className={`border-b-0 border-t-0 h-14 evt-navbar-back-to-uservoice ${hasCoreAccess ? '' : 'hidden'}`}
                key="back-to-uservoice"
                component="a"
                href="/admin"
                onClick={closeMenu}
              >
                <UserVoiceLogomark className="w-5 h-5 mr-2" />
                <ListItemText primary="Back to UserVoice" />
              </MenuItem>,
              <MenuItem
                className="border-b-0 h-14 pl-7 evt-navbar-logout"
                key="logout"
                onClick={() => { closeMenu(); logout(); }}
              >
                <ArrowRightBoxed className="w-4 h-4 mr-2.5" />
                <span className="text-sm">Log out</span>
              </MenuItem>,
            ]))}
          </ActionMenu>
        </div>
      </UserPermissionsContentWidth>
    </nav>
  );
}
